import React, { useEffect } from 'react';
import isValid from 'date-fns/isValid';
import isAfter from 'date-fns/isAfter';
import isEqual from 'date-fns/isEqual';
import parse from 'date-fns/parse';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import addDays from 'date-fns/addDays';
import { useImmer } from 'use-immer';

import { PopoverV2 } from '../PopoverV2';
import { MaskedInput } from '../InputV2/MaskedInput';
import { DatePicker } from './DatePicker';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Calendar } from '../../icons';
import type { DatePickerInputProps, DatePickerInputState, OnDatePickerChange } from './DatePicker.types';
import { formatDateToInputValue } from './DatePicker.utils';
import { KEYBOARD_KEYS } from '@withjoy/joykit/utils';
import { useLocaleContext } from '@localization/context/LocaleContext';
import { useDisclosure } from '@withjoy/joykit/hooks';
import { createPortal } from 'react-dom';

const DatePickerInput: React.FC<DatePickerInputProps> = (props: DatePickerInputProps) => {
  const locale = useLocaleContext();

  const {
    // Don't do anything with these props, just don't want to pass it to the MaskedInput
    filterDate,
    maxDate,
    minDate,
    monthsShown,
    overrides,
    // DatePickerProps
    'aria-describedby': ariaDescribedBy,
    'aria-errormessage': ariaErrorMessage,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    appendPickerToBody,
    dateFormat = locale.dateFormat,
    isDisabled,
    isInvalid,
    isRange,
    isReadOnly,
    isRequired,
    mask = locale.dateInputMask,
    onChange,
    placeholder = locale.dateFormat.toUpperCase(),
    rangeDisplayValue = 'start',
    value,
    startElement,
    endElement,
    placement = 'bottom',
    isOpen: _isOpen,
    onClose: _onClose,
    onOpen: _onOpen,
    onInputChange,
    enableCloseOnSelect,

    ...restProps
  } = props as DatePickerInputProps;

  const formatValue = (value: DatePickerInputProps['value']) => {
    return formatDateToInputValue(value, dateFormat);
  };

  const [{ startInputValue, endInputValue }, setState] = useImmer<DatePickerInputState>(() => ({ isOpen: !!_isOpen, ...formatValue(value) }));
  const { isOpen, onOpen, onClose } = useDisclosure({
    isOpen: _isOpen,
    onOpen: _onOpen,
    onClose: _onClose,
  });
  //////////////////////////////
  // State Handlers

  const openCalendar = useEventCallback(() => {
    onOpen();
  });

  const closeCalendar = useEventCallback(() => {
    onClose();
  });

  const setInputValueFromProp = useEventCallback((value: DatePickerInputProps['value']) => {
    setState(draft => {
      const formattedValues = formatValue(value);
      draft.startInputValue = formattedValues.startInputValue;
      draft.endInputValue = formattedValues.endInputValue;
    });
  });

  const updateInputValue = useEventCallback((value: string) => {
    setState(draft => {
      const inputField = isRange && rangeDisplayValue === 'end' ? 'endInputValue' : 'startInputValue';
      draft[inputField] = value;
    });
  });

  //////////////////////////////
  // Event handlers

  const handleOnChange: OnDatePickerChange = (dates, ev) => {
    // Handle automatic date range adjustment when dates[0] > dates[1]
    if (isRange && Array.isArray(dates) && Array.isArray(value)) {
      let [newStartValue, newEndValue] = dates;
      const [currentStartValue, currentEndValue] = value;

      // Calculate the previous delta between dates for maintaining the range
      const previousDelta = currentStartValue && currentEndValue ? differenceInCalendarDays(currentEndValue, currentStartValue) : 0;

      // If we're updating the start date and it becomes greater than the end date
      if (newStartValue && newEndValue && isAfter(newStartValue, newEndValue)) {
        // Automatically adjust end date to maintain the previous delta
        if (previousDelta > 0) {
          newEndValue = addDays(newStartValue, previousDelta);
          dates = [newStartValue, newEndValue];
        }
      }
    }

    onChange?.(dates, ev);
    if (enableCloseOnSelect) {
      if (!isRange) {
        closeCalendar();
      } else if (isRange && Array.isArray(dates) && dates[0] && dates[1]) {
        closeCalendar();
      }
    }
  };

  const handleOnInputChange = useEventCallback<React.ChangeEventHandler<HTMLInputElement>>(e => {
    const nextInputValue = e.target.value;

    updateInputValue(nextInputValue);
    onInputChange?.(nextInputValue);

    if (nextInputValue.replace(/(\s)*/g, '').length === dateFormat.replace(/(\s)*/g, '').length) {
      // Prevent early parsing

      const inputDate = parse(nextInputValue, dateFormat, new Date());

      if (!isValid(inputDate)) {
        return;
      }

      if (isRange && Array.isArray(value)) {
        let [startValue, endValue] = value;

        // Picking a date range can occur in one Date Picker, or across several composed pickers.
        // When segmented, pickers can be designated to choose specific range values - EG the "start" or "end" date

        if (rangeDisplayValue === 'start') {
          startValue = inputDate;
          if (endValue && !(isAfter(endValue, startValue) || isEqual(startValue, endValue))) {
            // If end value is before the start, reset the end
            endValue = null;
          }
        } else if (rangeDisplayValue === 'end') {
          endValue = inputDate;
          if (!startValue || !(isAfter(endValue, startValue) || isEqual(startValue, endValue))) {
            // If no start value, set same start/end.
            startValue = endValue;
          }
        }

        onChange?.([startValue, endValue]);
      } else {
        onChange?.(inputDate);
      }
    }
  });

  const handleOnInputKeyDown = useEventCallback<React.KeyboardEventHandler>(e => {
    if (!isOpen && e.key === KEYBOARD_KEYS.arrowDown) {
      // Open the calendar when the user presses down on the input
      openCalendar();
    }
  });

  //////////////////////////////
  // Lifecycle

  useEffect(() => {
    // When the value prop changes, sync the input value.
    setInputValueFromProp(value);
  }, [value, setInputValueFromProp]);

  //////////////////////////////
  // Render

  const inputValue = rangeDisplayValue === 'end' ? endInputValue : startInputValue;

  return (
    <PopoverV2
      isOpen={isOpen}
      onOpen={openCalendar}
      onClose={closeCalendar}
      placement={placement}
    >
      <PopoverV2.Trigger>
        {/* Masked text input that enforces date format as user types (e.g., auto-inserts "/" separators) and opens calendar popover when clicked */}
        <MaskedInput
          role="combobox"
          aria-haspopup="dialog"
          aria-describedby={ariaDescribedBy}
          aria-errormessage={ariaErrorMessage}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          isReadOnly={isReadOnly}
          isRequired={isRequired}
          mask={mask || ''}
          onChange={handleOnInputChange}
          onKeyDown={handleOnInputKeyDown}
          placeholder={placeholder}
          overrides={overrides?.Input}
          startElement={startElement ? startElement : undefined}
          endElement={endElement ? endElement : endElement === null ? undefined : <Calendar />}
          autoComplete="off"
          value={inputValue}
          {...restProps}
          inputVariant={props.inputVariant}
          label={props.label}
        />
      </PopoverV2.Trigger>

      {appendPickerToBody ? (
        createPortal(
          <PopoverV2.Content>
            <DatePicker
              {...props}
              onChange={handleOnChange}
            />
          </PopoverV2.Content>,
          document.body,
        )
      ) : (
        <PopoverV2.Content>
          <DatePicker
            {...props}
            onChange={handleOnChange}
          />
        </PopoverV2.Content>
      )}
    </PopoverV2>
  );
};

export { DatePickerInput };
